/* @flow */
import React from 'react';
import { graphql } from 'react-apollo';

import Heading from 'components/Heading';
import Link from 'components/Link';
import LinkedContentPreviewModal from 'components/LinkedContentPreviewModal';
import LoadingIndicator from 'components/LoadingIndicator';
import EventTracking from 'lib/EventTracking';
import { mountModal } from 'lib/modal';
import contentSourcesContentQuery from 'user/queries/contentSourcesContent.gql';

import ComposerIntegrationsContentEmpty from './ComposerIntegrationsContentEmpty';
import ComposerIntegrationsContentItem from './ComposerIntegrationsContentItem';

type Props = {
  data: ReactApolloData<contentSourcesContentQuery>,
  contentSource: *,
  query: string,
  addLinkedContent: (*) => void,
  onQueryFailed: () => void,
};

class ComposerIntegrationsContentList extends React.Component<Props> {
  hovercardTriggers = [];

  renderItem = (item: *, index: number) => {
    const handleItemClicked = () => {
      if (index in this.hovercardTriggers) {
        this.hovercardTriggers[index].hideLayer();
      }
      const { contentSource, addLinkedContent } = this.props;
      EventTracking.trackKey('account_linking.browse_content.item.click', {
        provider: contentSource.providerKey,
        composer_version: 'web_full_page',
      });

      if (item.contentUrl) {
        addLinkedContent(item);
      }
    };

    const handleThumbnailClicked = () => {
      if (index in this.hovercardTriggers) {
        this.hovercardTriggers[index].hideLayer();
      }
      mountModal(<LinkedContentPreviewModal linkedContent={item} />, this);
    };

    const registerHovercardTrigger = (trigger: *) => {
      this.hovercardTriggers[index] = trigger;
    };

    return (
      <ComposerIntegrationsContentItem
        key={index}
        item={item}
        handleItemClicked={handleItemClicked}
        handleThumbnailClicked={handleThumbnailClicked}
        registerHovercardTrigger={registerHovercardTrigger}
      />
    );
  };

  renderSection = (section: *, index: number) => {
    if (section.items.length === 0) {
      return null;
    }

    return (
      <div key={index}>
        <Heading
          level="2"
          levelStyle="3"
          align="left"
          className="ComposerIntegrationsContent-sectionHeading">
          {section.sectionTitle}
        </Heading>
        {section.items.map(this.renderItem)}
      </div>
    );
  };

  render() {
    const { data, query, onQueryFailed } = this.props;
    const { me, loading, refetch } = data;

    if (loading) {
      return <LoadingIndicator className="ComposerIntegrationsContent-loading" align="center" />;
    }

    if (data.error || !me || !me.composer || !me.composer.contentSourcesContent) {
      onQueryFailed();
      return (
        <ComposerIntegrationsContentEmpty header="Content didn't load properly">
          Sorry about that, please <Link onClick={() => refetch()}>try again.</Link>
        </ComposerIntegrationsContentEmpty>
      );
    }

    if (me.composer.contentSourcesContent.length === 0) {
      if (query.length > 0) {
        return (
          <ComposerIntegrationsContentEmpty header="No results found">
            Try using different search words
          </ComposerIntegrationsContentEmpty>
        );
      } else {
        return (
          <ComposerIntegrationsContentEmpty title="No content found.">
            To sign into a different account, disconned and reconnect this app in{' '}
            <Link weight="semibold" href="/settings/app_integrations">
              account settings
            </Link>
            .
          </ComposerIntegrationsContentEmpty>
        );
      }
    }
    return me.composer.contentSourcesContent.map(this.renderSection);
  }
}

const withContent = graphql(contentSourcesContentQuery, {
  options: ({ contentSource, query }) => ({
    fetchPolicy: 'network-only',
    variables: {
      source: contentSource.providerKey,
      query,
    },
  }),
});

export { ComposerIntegrationsContentList };
export default withContent(ComposerIntegrationsContentList);
